import { Button, Flex, Form, Input, Modal } from "antd";
import { RegisterUserType, TUser } from "api/services/models/types";
import { registerUser } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { ReactComponent as UserTypeIcon } from "Icons/vuesax/tick-square.svg";
import { useEffect, useState } from "react";
import { validatePassword } from "./validators";
import { TContact } from "api/services/models/types";
import AddOrUpdateProvider from "../providers/AddOrUpdateProvider";
import { getContactsForAccount } from "api/services/ContactService";

type TRegisterUserProps = {
  user: TUser;
  onClose: (user?: TUser) => void;
};

type TFields = {
  email: string;
  password: string;
};

const RegisterUser = ({ user, onClose }: TRegisterUserProps) => {
  const [form] = Form.useForm();
  const { accountId, webDUser } = useTDAuthentication();
  const [userType, setUserType] = useState<RegisterUserType>(RegisterUserType.Externaluser);
  const [error, setError] = useState<AxiosError | null>(null);
  const [editContactState, setEditContactState] = useState<{
      showModal: boolean;
      contact?: TContact;
    }>();
    const [contacts, setContacts] = useState<TContact[]>([]);

  const onChange = (userType: RegisterUserType) => {
    setUserType(userType);
    setError(null);
    form.resetFields();
  };
  const ruleType = userType === RegisterUserType.IntenalUser ? "string" : "email";
  form.setFields([{ name: "email", value: userType === RegisterUserType.IntenalUser ? `${user.userId.toLowerCase()}.${user.accountId.toLowerCase()}` : "" }]);
  form.setFields([{ name: "recoveryEmail", value: userType === RegisterUserType.IntenalUser && user.email ? `${user.email}` : "" }]);

  const onFinish = (values: TFields) => {
    registerUser({
      email:
        userType === RegisterUserType.Externaluser
          ? values.email
          : `${values.email}@transdynex.com`,
      userId: user.userId,
      accountId: user.accountId,
      displayName: `${user.userId} ${user.accountId}`,
      isInternalUser: userType === RegisterUserType.IntenalUser,
      password: userType === RegisterUserType.IntenalUser ? values.password : undefined
    })
      .then(user => onClose(user))
      .catch(setError);
  };

  useEffect(() => {
      if (accountId) {
        setError(null);
        getContactsForAccount(accountId)
          .then(setContacts)
          .catch(setError)
      }
  }, [accountId]);

  const closeEditContactModal = (contact?: TContact) => {
    setEditContactState({ showModal: false });
    if (contact) {
      user.email = contact.email;
    }
  };

  return (
    <Modal
      width={444}
      title={`Register User: ${user.userId}@${user.accountId}`}
      maskClosable={false}
      open={true}
      footer={null}
      onCancel={() => onClose()}
      style={{ padding: "1rem" }}>
      {webDUser?.isInternalUser() && (
        <Flex
          wrap
          gap="middle"
          align="start"
          justify="space-between"
          style={{ marginBottom: "16px" }}>
          <Button
            size="large"
            onClick={() => onChange(RegisterUserType.IntenalUser)}
            className="user-type-button"
            style={{
              color: userType === RegisterUserType.IntenalUser ? "white" : "black",
              backgroundColor:
                userType === RegisterUserType.IntenalUser ? "#3498db" : "lightgray"
            }}>
            {userType === RegisterUserType.IntenalUser && <UserTypeIcon />}
            Local User
          </Button>

          <Button
            size="large"
            onClick={() => onChange(RegisterUserType.Externaluser)}
            className="user-type-button"
            style={{
              color: userType === RegisterUserType.Externaluser ? "white" : "black",
              backgroundColor:
                userType === RegisterUserType.Externaluser ? "#3498db" : "lightgray"
            }}>
            {userType === RegisterUserType.Externaluser && <UserTypeIcon />}
            External User
          </Button>
        </Flex>
      )}

      {error && <ErrorViewer error={error} />}
      <Form
        form={form}
        name="register-user"
        layout="vertical"
        style={{ marginTop: "16px" }}>
        <Form.Item
          name={["email"]}
          label={
            <label htmlFor="email" className="input-label">
              Email
            </label>
          }
          rules={[{ required: true }, { type: ruleType }]}>
          <Input
            className="filter-input-transparent"
            autoComplete={"false"}
            aria-autocomplete={"none"}
            addonAfter={userType === RegisterUserType.IntenalUser ? "@transdynex.com" : ""}
            readOnly={userType === RegisterUserType.IntenalUser ? true : false}
          />
        </Form.Item>

        <Form.Item
          name={"password"}
          label={
            <label htmlFor="password" className="input-label">
              Password
            </label>
          }
          hidden={userType === RegisterUserType.Externaluser}
          rules={[{ required: true }, validatePassword(userType)]}>
          <Input.Password className="filter-input-transparent" />
        </Form.Item>

        <Form.Item
          name={"confirmPassword"}
          label={
            <label htmlFor="confirmPassword" className="input-label">
              Confirm Password
            </label>
          }
          hidden={userType === RegisterUserType.Externaluser}
          rules={[
            { required: userType === RegisterUserType.IntenalUser },
            ({ getFieldValue }) => ({
              validator(_, value, callback) {
                if (!value || getFieldValue("password") === value) {
                  callback();
                }
                callback("The new password that you entered do not match!");
              }
            })
          ]}>
          <Input.Password className="filter-input-transparent" />
        </Form.Item>

        <Form.Item
          label={
            <label htmlFor="recoveryEmail" className="input-label">
              Recovery Email
            </label>
          }
          hidden={userType === RegisterUserType.Externaluser}>

          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Item name="recoveryEmail" noStyle>
              <Input 
                className="filter-input-transparent" 
                style={{ flex: 1 }} 
                readOnly={userType === RegisterUserType.IntenalUser ? true : false} 
              />
            </Form.Item>
            <Button 
              type="primary"
              style={{ marginLeft: 8 }}
              onClick={() => setEditContactState({ showModal: true, contact: contacts.find(contact => contact.contactId === user.contactId) })}
             >Update Email</Button>
          </div>
        </Form.Item>

        <Form.Item label={null} style={{ textAlign: "right" }}>
          <Button type="primary" htmlType="submit"
          onClick={() => (onFinish(form.getFieldsValue() as TFields))}
          >
            Register
          </Button>
        </Form.Item>
      </Form>
      {editContactState?.showModal && (
        <AddOrUpdateProvider
          contact={editContactState.contact}
          onClose={closeEditContactModal}
        />
      )}
    </Modal>
  );
};

export default RegisterUser;
